import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, hasPermission } = useAuth();
  const { pageTitle } = usePageContext();
  const location = useLocation();

  const sidebarStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '280px',
    height: '100vh',
    background: 'linear-gradient(180deg, #1e293b 0%, #334155 100%)',
    color: 'white',
    zIndex: 100,
    overflowY: 'auto',
    boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
    transform: sidebarOpen || window.innerWidth > 992 ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s ease'
  };

  const mainContentStyle = {
    marginLeft: window.innerWidth > 992 ? '280px' : '0',
    minHeight: '100vh'
  };

  const menuItems = [
    { name: 'Dashboard', href: '/dashboard', icon: 'fas fa-tachometer-alt', permission: null },
    { name: 'Admin Management', href: '/admin', icon: 'fas fa-users-cog', permission: { module: 'admin', action: 'read' } },
    { name: 'Employees', href: '/employees', icon: 'fas fa-users', permission: { module: 'employee', action: 'read' } },
    { name: 'Clients', href: '/clients', icon: 'fas fa-building', permission: { module: 'client', action: 'read' } },
    { name: 'Vendors', href: '/vendors', icon: 'fas fa-truck', permission: { module: 'vendor', action: 'read' } },
    { name: 'Recruiters', href: '/recruiters', icon: 'fas fa-user-tie', permission: { module: 'recruiter', action: 'read' } },
    { name: 'Payroll Conditions', href: '/payroll-conditions', icon: 'fas fa-calculator', permission: { module: 'condition', action: 'read' } },
    { name: 'Invoices', href: '/invoices', icon: 'fas fa-file-invoice', permission: { module: 'invoice', action: 'read' } },
    { name: 'Expenses', href: '/expenses', icon: 'fas fa-receipt', permission: { module: 'expense', action: 'read' } },
    { name: 'Expense Recipients', href: '/expense-recipients', icon: 'fas fa-address-book', permission: { module: 'expense', action: 'read' } },
    { name: 'Payment History', href: '/payment-history', icon: 'fas fa-credit-card', permission: { module: 'expense', action: 'read' } },
    { name: 'Expense Analytics', href: '/expense-analytics', icon: 'fas fa-chart-pie', permission: { module: 'expense', action: 'read' } },
    { name: 'Reports', href: '/reports', icon: 'fas fa-chart-bar', permission: null },
    { name: 'How It Works', href: '/how-it-works', icon: 'fas fa-question-circle', permission: null }
  ];

  const filteredMenuItems = menuItems.filter(item => 
    !item.permission || hasPermission(item.permission.module, item.permission.action)
  );

  return (
    <>
      {/* Sidebar */}
      <div style={sidebarStyle}>
        <div style={{ padding: '24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 style={{ margin: 0, fontWeight: 'bold', background: 'linear-gradient(45deg, #60a5fa, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>CALIBER</h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', opacity: 0.7 }}>Payroll Management</p>
        </div>
        
        <div style={{ padding: '16px 0' }}>
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 24px',
                  color: isActive ? '#60a5fa' : 'rgba(255,255,255,0.8)',
                  textDecoration: 'none',
                  background: isActive ? 'rgba(96,165,250,0.1)' : 'transparent',
                  borderRight: isActive ? '3px solid #60a5fa' : 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.target.style.background = 'rgba(255,255,255,0.05)';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'rgba(255,255,255,0.8)';
                  }
                }}
              >
                <i className={item.icon} style={{ marginRight: '12px', width: '16px' }}></i>
                {item.name}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Header */}
        <div className="bg-white shadow-sm border-bottom">
          <div className="container-fluid px-4 py-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-primary d-lg-none me-3"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <i className="fas fa-bars"></i>
                </button>
                <div>
                  <h1 className="h4 mb-0 fw-bold text-dark">{pageTitle}</h1>
                  <small className="text-muted">Caliber › {pageTitle}</small>
                </div>
              </div>
              
              <div className="dropdown">
                <button
                  className="btn btn-outline-primary"
                  type="button"
                  data-bs-toggle="dropdown"
                >
                  <i className="fas fa-user-circle me-2"></i>
                  <span className="d-none d-md-inline">{user?.name}</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li><span className="dropdown-item-text">Role: {user?.role}</span></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button className="dropdown-item" onClick={logout}>
                      <i className="fas fa-sign-out-alt me-2"></i>
                      Logout
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="container-fluid p-4">
          {children}
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 99
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <style>{`
        @media (min-width: 992px) {
          .sidebar-open { transform: translateX(0) !important; }
        }
        @media (max-width: 991px) {
          .sidebar-mobile { transform: translateX(-100%) !important; }
        }
      `}</style>
    </>
  );
};

export default Layout;